import {
  Component,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { PageHeaderComponent } from '../../../../../../@commons/@components/app-header/page-header/page-header.component';

import { SharedModule } from '../../../../../../@commons/ui-components/shared.module';
import { FormsModule } from '@angular/forms';
import { LocationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/location-api';
import { TranslatePipe } from '@ngx-translate/core';
import { LocationHomeIconComponent } from '../../../../../../@icons/location-home-icon.component';
import { LocationType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/location-type.enum';
import { SunchineLevels } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/sunchine-levels.enum';
import { StorageService } from '../../../../../../@commons/@services/front/storage.service';
import { LocationResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/location.response';
import { ChoiceOption } from '../../../../../../@commons/ui-components/choice-selector/choice-selector.component';
import { readPremiumRefusal } from '../../../../../../@commons/@services/front/premium-error.util';
import { PremiumTeaserComponent } from '../../../../../../@commons/ui-components/premium-teaser/premium-teaser.component';
import { PremiumToastComponent } from '../../../../../../@commons/@toasts/premium-toast/premium-toast.component';

@Component({
  selector: 'vw-user-adress-location-form',
  imports: [
    PremiumTeaserComponent,
    PremiumToastComponent,
    PageHeaderComponent,
    SharedModule,
    FormsModule,
    TranslatePipe,
    LocationHomeIconComponent
],
  templateUrl: './user-adress-location-form.component.html',
  styleUrl: './user-adress-location-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: LocationApi,
      useClass: LocationApi,
    },
  ],
})
export class UserAdressLocationFormComponent implements OnChanges, OnInit {
  /** Invitation premium affichee en contexte quand la limite est atteinte. */
  premiumMessageKey: string | null = null;
  premiumPaywallOpen = false;

  openPaywall(): void {
    this.premiumPaywallOpen = true;
  }

  @Input() location?: any;
  /** Affiche l'action « Supprimer cette zone » (uniquement en édition, ex. depuis Mon Potager). */
  @Input() showDelete = false;
  /** Libellé du retour propre au parcours (« Retour à la répartition ») ; sinon « Retour ». */
  @Input() backLabel?: string | null;
  /**
   * Intégré dans un assistant (nouvelle zone) : ni retour, ni titre, ni encart,
   * l'assistant porte déjà son en-tête et son explication.
   */
  @Input({ transform: (value: boolean | string) => value !== false && value !== 'false' })
  embedded = false;
  @Output() closeLocationForm = new EventEmitter<LocationResponse>();
  /** Émis quand l'utilisateur demande la suppression ; l'hôte gère la confirmation + l'appel API. */
  @Output() deleteLocation = new EventEmitter<any>();
  isSubmittingLocation: boolean = false;
  submitAttempted: boolean = false;
  /** L'enregistrement a échoué (hors refus Premium) : la saisie reste, le message dit de réessayer. */
  saveFailed = false;

  // Types de zone = des LIEUX (une zone regroupe des planches). « Bac potager » est une planche, pas une zone → retiré.
  typeOptions: ChoiceOption[] = [
    {
      label: 'profile.addresses.edit-address.edit-locations.locations.GARDEN',
      value: 'GARDEN',
      icon: '🌱',
    },
    {
      label:
        'profile.addresses.edit-address.edit-locations.locations.GREENHOUSE',
      value: 'GREENHOUSE',
      icon: '🏡',
    },
    {
      label: 'profile.addresses.edit-address.edit-locations.locations.BALCONY',
      value: 'BALCONY',
      icon: '🪟',
    },
    {
      label: 'profile.addresses.edit-address.edit-locations.locations.TERRACE',
      value: 'TERRACE',
      icon: '☀️',
    },
    {
      label: 'profile.addresses.edit-address.edit-locations.locations.INDOOR',
      value: 'INDOOR',
      icon: '🛋️',
    },
  ];

  /**
   * Exposition : une seule liste pour la zone et la planche (revue design, motif 9),
   * celle de la planche : Ombre, Mi-ombre, Ensoleillé, Plein soleil.
   */
  sunshineOptions: ChoiceOption[] = ['SHADED', 'PARTIALY_SHADED', 'SUNNY', 'VERY_SUNNY'].map((value) => ({
    value,
    label: `garden.beds.enums.exposure.${value}`,
  }));

  /** Exposition proposée pour une nouvelle zone : le cas courant, un jardin au soleil. */
  static readonly DEFAULT_SUNSHINE = 'SUNNY';

  name: string = '';
  selectedType: string = 'GARDEN';
  selectedSunshine: string = UserAdressLocationFormComponent.DEFAULT_SUNSHINE;
  width?: number;
  length?: number;

  constructor(
    private locationApi: LocationApi,
    private storageService: StorageService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    window.scrollTo(0, 0);
    this.setDefaultValues();
  }

  ngOnInit() {
    window.scrollTo(0, 0);
    this.setDefaultValues();
  }

  setDefaultValues() {
    this.submitAttempted = false;
    this.saveFailed = false;
    if (this.location) {
      this.name = this.location.name ?? '';
      // Repli sur le 1er type si l'ancien type (ex. PLANTER_BOX) n'est plus proposé.
      const hasType = this.typeOptions.some(
        (o) => o.value === this.location.locationType,
      );
      this.selectedType = hasType
        ? this.location.locationType
        : this.typeOptions[0].value;
      const hasSun = this.sunshineOptions.some(
        (o) => o.value === this.location.sunchineLevels,
      );
      this.selectedSunshine = hasSun
        ? this.location.sunchineLevels
        : UserAdressLocationFormComponent.DEFAULT_SUNSHINE;
      this.width = this.location.width ?? undefined;
      this.length = this.location.length ?? undefined;
    } else {
      this.name = '';
      this.selectedType = this.typeOptions[0].value;
      this.selectedSunshine = UserAdressLocationFormComponent.DEFAULT_SUNSHINE;
      this.width = undefined;
      this.length = undefined;
    }
  }

  get isNameValid(): boolean {
    return !!this.name && this.name.trim().length >= 2;
  }

  get derivedSurface(): number | null {
    return this.width && this.length
      ? Math.round(this.width * this.length)
      : null;
  }

  onCloseLocationForm() {
    this.closeLocationForm.emit();
  }

  onDeleteLocation() {
    this.deleteLocation.emit(this.location);
  }

  onNameChange(value: string) {
    this.name = value;
  }

  async onSubmitLocationForm() {
    if (this.isSubmittingLocation) return;
    this.submitAttempted = true;
    if (!this.isNameValid) {
      return;
    }
    this.isSubmittingLocation = true;
    this.saveFailed = false;
    this.premiumMessageKey = null;
    let editedLocation: LocationResponse | null = null;
    try {
      if (this.location) {
        editedLocation = await this.locationApi.update({
          uid: this.location.uid,
          name: this.name.trim(),
          locationType: this.selectedType as LocationType,
          sunchineLevels: this.selectedSunshine as SunchineLevels,
          width: this.width || undefined,
          length: this.length || undefined,
        });
      } else {
        const addressUid = this.storageService.getItem('address-uid');
        // La surface n'est plus codée en dur : elle est dérivée des dimensions côté back.
        editedLocation = await this.locationApi.create({
          name: this.name.trim(),
          locationType: this.selectedType as LocationType,
          sunchineLevels: this.selectedSunshine as SunchineLevels,
          addressUid,
          width: this.width || undefined,
          length: this.length || undefined,
        } as any);
        this.location = undefined;
      }
    } catch (error) {
      const refusal = readPremiumRefusal(error);
      if (!refusal) {
        // Panne ou refus du serveur : on le dit, sans fermer ni perdre la saisie.
        console.error('Zone non enregistrée', error);
        this.saveFailed = true;
        return;
      }
      this.premiumMessageKey = refusal.messageKey ?? 'premium.gate.zone';
      this.isSubmittingLocation = false;
      // On ne ferme pas le formulaire : l'utilisateur voit l'invitation en
      // contexte, sans perdre ce qu'il a saisi.
      return;
    } finally {
      this.isSubmittingLocation = false;
    }
    this.closeLocationForm.emit(editedLocation);
  }
}
